import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useSession } from '../store/session';
import { Spinner } from '../ui/common';

const PHONE_RE = /^[6-9]\d{9}$/;
const RESEND_SECONDS = 30;

/**
 * Phone + OTP. No passwords and no email — the phone number is the identity,
 * which is also what makes payouts attributable to a real person later.
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';
  const completeLogin = useSession((s) => s.completeLogin);

  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  const sendOtp = async (): Promise<void> => {
    if (!PHONE_RE.test(phone)) {
      setError('Enter a valid 10-digit mobile number.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.otpSend(phone);
      setStep('code');
      setCooldown(RESEND_SECONDS);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send the code. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (): Promise<void> => {
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code we sent you.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await api.otpVerify(phone, code);
      completeLogin(session);
      navigate(next, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not verify the code. Try again.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page page-narrow" style={{ maxWidth: 420, paddingTop: 56 }}>
      <div className="center mb-3">
        <div
          className="brand-mark"
          style={{ width: 44, height: 44, fontSize: 23, margin: '0 auto 16px', borderRadius: 11 }}
        >
          D
        </div>
        <h1 className="display" style={{ fontSize: 32 }}>
          {step === 'phone' ? 'Open your book' : 'Enter the code'}
        </h1>
        <p className="t-md muted mt-1">
          {step === 'phone'
            ? 'A number and a six-digit code. No password, no email, no practice tier.'
            : `We sent a 6-digit code to +91 ${phone}.`}
        </p>
      </div>

      <div className="card">
        {step === 'phone' ? (
          <form
            className="col"
            style={{ gap: 14 }}
            onSubmit={(e) => {
              e.preventDefault();
              void sendOtp();
            }}
          >
            <div className="field">
              <label htmlFor="phone">Mobile number</label>
              <div className="row" style={{ gap: 8 }}>
                <span
                  className="input num center"
                  style={{
                    width: 62,
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'var(--surface-2)',
                  }}
                >
                  +91
                </span>
                <input
                  id="phone"
                  className={`input num${error ? ' input-error' : ''}`}
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="98765 43210"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                    setError(null);
                  }}
                />
              </div>
            </div>
            {error ? <div className="error-text">{error}</div> : null}
            <button className="btn btn-primary btn-lg btn-block" disabled={busy || phone.length !== 10}>
              {busy ? <Spinner /> : 'Continue'}
            </button>
          </form>
        ) : (
          <form
            className="col"
            style={{ gap: 14 }}
            onSubmit={(e) => {
              e.preventDefault();
              void verify();
            }}
          >
            <div className="field">
              <label htmlFor="code">6-digit code</label>
              <input
                id="code"
                ref={codeRef}
                className={`input num center${error ? ' input-error' : ''}`}
                style={{ letterSpacing: '0.5em', fontSize: 22, fontWeight: 600 }}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="––––––"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setError(null);
                }}
              />
            </div>
            {error ? <div className="error-text">{error}</div> : null}
            <button className="btn btn-primary btn-lg btn-block" disabled={busy || code.length !== 6}>
              {busy ? <Spinner /> : 'Verify and sign in'}
            </button>
            <div className="between">
              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setStep('phone');
                  setCode('');
                  setError(null);
                }}
              >
                Change number
              </button>
              <button
                type="button"
                className="btn-link"
                disabled={cooldown > 0 || busy}
                style={cooldown > 0 ? { color: 'var(--text-3)', cursor: 'default' } : undefined}
                onClick={() => void sendOtp()}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}
      </div>


      {step === 'phone' ? (
        <p className="t-sm muted mt-3 center" style={{ lineHeight: 1.6 }}>
          Your account starts at zero buying power. Nothing can be traded until you deposit, and
          settled cash stays in a wallet you can withdraw from.
        </p>
      ) : null}
    </div>
  );
}
